import type { CasinoProfitBreakdown } from "@utopia-well-done-ltd/contracts";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { readUrlFilters, toIsoOrUndefined, writeUrlFilters } from "../components/ExplorerTools.js";
import { asAmount } from "../format.js";

interface Filters {
  currency?: string;
  from?: string;
  game?: string;
  table?: string;
  to?: string;
}

const emptyForm = { currency: "", from: "", game: "", table: "", to: "" };
const start = (date: string) => toIsoOrUndefined(`${date}T00:00:00.000Z`);
const end = (date: string) => toIsoOrUndefined(`${date}T23:59:59.999Z`);
const asFilters = (form: typeof emptyForm): Filters => {
  const from = start(form.from);
  const to = end(form.to);
  return {
    ...(form.currency ? { currency: form.currency } : {}),
    ...(from ? { from } : {}),
    ...(form.game ? { game: form.game } : {}),
    ...(form.table.trim() ? { table: form.table.trim() } : {}),
    ...(to ? { to } : {}),
  };
};

function margin(ggr: string, wagered: string) {
  const stake = BigInt(wagered);
  if (stake === 0n) return "—";
  const basisPoints = (BigInt(ggr) * 10_000n) / stake;
  const sign = basisPoints < 0n ? "−" : "";
  const absolute = basisPoints < 0n ? -basisPoints : basisPoints;
  return `${sign}${absolute / 100n}.${(absolute % 100n).toString().padStart(2, "0")}%`;
}

export function Profit() {
  const [rows, setRows] = useState<CasinoProfitBreakdown[] | null>(null);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const initialForm = useRef(readUrlFilters(emptyForm)).current;
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const loadGeneration = useRef(0);

  const load = useCallback(async (filters: Filters) => {
    const requestGeneration = ++loadGeneration.current;
    setPending(true);
    setError(null);
    try {
      const breakdown = (await api.casinoProfit(filters)).breakdown;
      if (loadGeneration.current !== requestGeneration) return;
      setRows(breakdown);
      setCurrencies((current) =>
        [...new Set([...current, ...breakdown.map((row) => row.currency)])].sort(),
      );
    } catch (caught) {
      if (loadGeneration.current !== requestGeneration) return;
      setError(caught instanceof ApiError ? caught.message : "Could not load casino profit.");
    } finally {
      if (loadGeneration.current === requestGeneration) setPending(false);
    }
  }, []);

  useEffect(() => {
    void load(asFilters(initialForm));
  }, [initialForm, load]);

  function apply(event: FormEvent) {
    event.preventDefault();
    writeUrlFilters(form);
    void load(asFilters(form));
  }

  function clear() {
    setForm(emptyForm);
    writeUrlFilters(emptyForm);
    void load({});
  }

  const totals = new Map<
    string,
    { ggr: bigint; returned: bigint; rounds: number; wagered: bigint }
  >();
  for (const row of rows ?? []) {
    const current = totals.get(row.currency) ?? { ggr: 0n, returned: 0n, rounds: 0, wagered: 0n };
    current.ggr += BigInt(row.grossGamingRevenue.amount);
    current.returned += BigInt(row.returnedToPlayers.amount);
    current.rounds += row.roundCount;
    current.wagered += BigInt(row.wagered.amount);
    totals.set(row.currency, current);
  }

  return (
    <section className="body">
      <form className="transaction-filters profit-filters" onSubmit={apply}>
        <label className="field">
          <span>Game</span>
          <select
            className="input"
            onChange={(event) => setForm({ ...form, game: event.target.value })}
            value={form.game}
          >
            <option value="">All games</option>
            <option value="blackjack">Blackjack</option>
            <option value="roulette">Roulette</option>
            <option value="slots">Slots</option>
            <option value="poker">Poker</option>
          </select>
        </label>
        <label className="field">
          <span>Table</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, table: event.target.value })}
            placeholder="Table prefix"
            value={form.table}
          />
        </label>
        <label className="field">
          <span>Currency</span>
          <select
            className="input"
            onChange={(event) => setForm({ ...form, currency: event.target.value })}
            value={form.currency}
          >
            <option value="">All platform currencies</option>
            {currencies.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>From</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, from: event.target.value })}
            type="date"
            value={form.from}
          />
        </label>
        <label className="field">
          <span>To</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, to: event.target.value })}
            type="date"
            value={form.to}
          />
        </label>
        <div className="transaction-filter-actions">
          <button className="btn" disabled={pending} type="submit">
            {pending ? "Loading…" : "Apply filters"}
          </button>
          <button className="btn btn-quiet" disabled={pending} onClick={clear} type="button">
            Clear
          </button>
        </div>
      </form>

      {error ? <p className="banner">{error}</p> : null}

      <p className="banner banner-quiet">
        GGR is calculated from settled wallet movements and corrections. NGR is not shown until
        bonus, payment-provider fee and tax costs have authoritative ledger sources.
      </p>

      {[...totals].map(([currency, total]) => (
        <div className="card profit-summary" key={currency}>
          <div className="fact">
            <div className="fact-label">Gross gaming revenue</div>
            <div className="fact-value mono-12">
              {asAmount({ amount: total.ggr.toString(), currency })}
            </div>
          </div>
          <div className="fact">
            <div className="fact-label">Settled wagered</div>
            <div className="fact-value mono-12">
              {asAmount({ amount: total.wagered.toString(), currency })}
            </div>
          </div>
          <div className="fact">
            <div className="fact-label">Returned to players</div>
            <div className="fact-value mono-12">
              {asAmount({ amount: total.returned.toString(), currency })}
            </div>
          </div>
          <div className="fact">
            <div className="fact-label">GGR margin</div>
            <div className="fact-value mono-12">
              {margin(total.ggr.toString(), total.wagered.toString())}
            </div>
          </div>
          <div className="fact">
            <div className="fact-label">Rounds</div>
            <div className="fact-value mono-12">{total.rounds}</div>
          </div>
        </div>
      ))}

      <div className="card table-scroll">
        {!rows ? (
          <p className="empty">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="empty">No settled gaming activity matches these filters.</p>
        ) : (
          <table className="profit-table mobile-cards">
            <thead>
              <tr>
                <th>Game</th>
                <th>Table</th>
                <th>Players</th>
                <th>Rounds</th>
                <th>Wagered</th>
                <th>Returned</th>
                <th>GGR</th>
                <th>Margin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.currency}:${row.game}:${row.tableId ?? "single"}`}>
                  <td className="label-13" data-label="Game">
                    {row.game}
                  </td>
                  <td className="mono-12 dim" data-label="Table">
                    {row.tableId ?? "Single player"}
                  </td>
                  <td className="mono-12" data-label="Players">
                    {row.playerCount}
                  </td>
                  <td className="mono-12" data-label="Rounds">
                    {row.roundCount}
                  </td>
                  <td className="mono-12" data-label="Wagered">
                    {asAmount(row.wagered)}
                  </td>
                  <td className="mono-12" data-label="Returned">
                    {asAmount(row.returnedToPlayers)}
                  </td>
                  <td className="mono-12 profit-ggr" data-label="GGR">
                    {asAmount(row.grossGamingRevenue)}
                  </td>
                  <td className="mono-12" data-label="Margin">
                    {margin(row.grossGamingRevenue.amount, row.wagered.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
